<template>
  <a-modal
    :visible="true"
    :title="`Editar empresa - ${record.name}`"
    width="600px"
    :mask-closable="false"
    centered
    :keyboard="false"
    @cancel="handleConfirmationClose"
  >
    <div class="colorStatus">
      <span
        >Estado:
        <a-tag :color="colorStatus(record.status)">
          {{ record.status === UserStatus.active ? "Activo" : "Inactivo" }}</a-tag
        >
      </span>
    </div>
    <div ref="scrollWrapper">
      <CustomScrollbar height="500px">
        <a-form class="form" layout="vertical" ref="formRef" :model="formState" :rules="rules">
        <image-picker
          :value="imageFile"
          @update:value="imageFile = $event"
          :src="imageSrc || imageDefault"
          @onSelectedImage="handleSelectedImagen"
          :selectedImage="selectedImage"
          :isEntity="isEntity"
        />

        <a-form-item label="Nombre de la empresa" name="name">
          <a-input
            v-model:value="formState.name"
            placeholder="Escribir nombre de la empresa"
            :disabled="record.status === UserStatus.inactive"
            @input="showConfirmModal = true"
          />
        </a-form-item>
        <a-form-item label="Industria" name="type_industry">
          <a-select
            v-model:value="formState.type_industry"
            placeholder="Seleccione tipo de industria"
            :disabled="record.status === UserStatus.inactive"
            @change="showConfirmModal = true"
            @dropdownVisibleChange="handleDropdownVisibleChange"
          >
            <a-select-option v-for="industry in industries" :key="industry.id">
              {{ industry.name }}
            </a-select-option>
          </a-select>
        </a-form-item>

        <a-row :gutter="24">
          <a-col :span="24">
            <a-form-item label="Teléfono de contacto" name="phone">
              <a-row :gutter="14" type="flex">
                <a-col>
                  <selector-country
                    :code="codePhone"
                    @selected="codePhone = $event"
                    :disabled="record.status === UserStatus.inactive"
                    style="width: 135px"
                    @change="showConfirmModal = true"
                    @dropdownVisibleChange="handleDropdownVisibleChange"
                  />
                </a-col>
                <a-col flex="1">
                  <a-input
                    v-model:value="formState.phone"
                    placeholder="Número de teléfono"
                    :disabled="record.status === UserStatus.inactive"
                    type="number"
                    @input="showConfirmModal = true"
                  />
                </a-col>
              </a-row>
            </a-form-item>
          </a-col>
        </a-row>

        <a-row :gutter="12">
          <a-col :span="24">
            <a-form-item name="country">
              <template #label>
                <span class="required">*</span>
                <span>
                  País de la empresa
                  <a-tooltip>
                    <InfoCircleOutlined />
                    <template #title>
                      <span class="info-circle">
                        Selecciona el país principal donde se ubica esta empresa
                      </span>
                    </template>
                  </a-tooltip>
                </span>
              </template>
              <selector-country-business
                :code="country"
                @onSelected="country = $event"
                placeholder="Seleccionar país"
                @change="showConfirmModal = true"
                @keypress="handleSearchCountry($event)"
                @dropdownVisibleChange="handleDropdownVisibleChange"
              />
            </a-form-item>
          </a-col>
        </a-row>
        <a-form-item label="Email" name="email">
          <a-input
            v-model:value="formState.email"
            placeholder="Escribir el correo de la empresa"
            :disabled="record.status === UserStatus.inactive"
            @input="showConfirmModal = true"
          />
        </a-form-item>

        <AIEntityPromptInput
          v-if="hasAIIntegration"
          v-model:value="formState.description"
          v-model:keywords="formState.keywords"
        />
      </a-form>
      </CustomScrollbar>
    </div>
    <!-- Fotter Modal -->
    <template v-slot:footer>
      <div class="control__user">
        <div>
          <a-button key="back" @click="handleConfirmationClose"> Cancelar </a-button>
          <a-button key="submit" type="primary" :loading="loading" @click="handleOk">
            Guardar
          </a-button>
        </div>
      </div>
    </template>
  </a-modal>
</template>

<script>
import ImagePicker from "@/app/shared/components/molecules/ImagePicker.vue";
import CustomScrollbar from "@/app/shared/components/organisms/CustomScrollbar.vue";
import { defineComponent, ref, reactive, toRaw, createVNode } from "vue";
import { InfoCircleOutlined, QuestionCircleOutlined } from "@ant-design/icons-vue";
import { UserStatus } from "@/app/shared/utils/enums";
import SelectorCountryBusiness from "@/app/shared/components/selectors/SelectorCountryBusiness.vue";
import { Modal } from "ant-design-vue";
import SelectorCountry from "@/app/shared/components/selectors/SelectorCountry.vue";
import AIEntityPromptInput from "@/app/integrations/components/inputs/AIEntityPromptInput.vue";
import briefcaseBlueImage from "@/app/entities/assets/briefcase-blue.svg?url";

export default defineComponent({
  components: {
    ImagePicker,
    CustomScrollbar,
    InfoCircleOutlined,
    SelectorCountry,
    SelectorCountryBusiness,
    AIEntityPromptInput,
  },
  props: {
    record: {
      type: Object,
      required: true,
    },
  },
  setup(props, context) {
    const formRef = ref();
    const visibleDelete = ref(false);
    const country = ref(props.record.country?.id);
    const isEntity = ref(true);
    const showConfirmModal = ref(false);
    const selectedImage = ref(false);
    const handleDeleteEntitie = () => {
      visibleDelete.value = true;
    };
    const formState = reactive({
      name: props.record.name,
      phone: props.record.phone,
      email: props.record.email,
      type_industry: props.record.type_industry,
      description: props.record.description,
      keywords: props.record.keywords,
    });
    const rules = {
      name: [{ required: true, message: "Campo requerido" }],
      type_industry: [{ required: true, message: "Campo requerido" }],
    };
    const handleConfirmationClose = () => {
      if (showConfirmModal.value) {
        Modal.confirm({
          title: "¿Deseas descartar los cambios realizados?",
          content: "Al cerrar no se guardarán los datos previamente ingresados",
          icon: createVNode(QuestionCircleOutlined),
          onOk: () => {
            handleCancel();
          },
          centered: true,
          okText: "Sí, descartar cambios",
          cancelText: "No, seguir editando",
        });
      } else {
        handleCancel();
      }
    };
    const handleCancel = () => {
      context.emit("onCloseModal");
      selectedImage.value = false;
      showConfirmModal.value = false;
    };
    return {
      formRef,
      formState,
      rules,
      visibleDelete,
      handleDeleteEntitie,
      country,
      handleConfirmationClose,
      showConfirmModal,
      selectedImage,
      isEntity,
    };
  },
  data() {
    return {
      loading: false,
      codePhone: 1,
      maxObservation: 120,
      imageFile: undefined,
      imageSrc: "",
      UserStatus,
      isAnyDropdownOpen: false,
      dropdownCloseTimer: null,
    };
  },
  mounted() {
    this.$refs.scrollWrapper.addEventListener("wheel", this.preventScroll, { capture: true, passive: false });
  },
  beforeUnmount() {
    this.$refs.scrollWrapper?.removeEventListener("wheel", this.preventScroll, { capture: true });
  },
  computed: {
    countries() {
      return this.$store.getters["getAllCountries"];
    },
    industries() {
      console.log(this.$store.getters["getAllIndustries"]);
      return this.$store.getters["getAllIndustries"];
    },
    hasAIIntegration() {
      const user = this.$store.getters["getDataUser"];

      return user.customer_enable_summary;
    },
    imageDefault() {
      let url = "";
      url = briefcaseBlueImage;
      return url;
    },
  },
  created() {
    this.setFieldsValue();
  },
  methods: {
    handleSearchCountry(e) {
      const char = String.fromCharCode(e.keyCode);
      if (/^[A-Za-z]+$/.test(char)) return true;
      else e.preventDefault();
    },
    colorStatus(status) {
      return status === UserStatus.active ? "green" : "red";
    },
    async handleReactiveEntitie() {
      try {
        const body = {
          status: UserStatus.active,
        };
        await this.$store.dispatch("updateStatusEntitie", {
          id: this.record.id,
          params: body,
        });

        this.$message.success(`Se activó la empresa ”${this.record.name}” correctamente`);
      } catch (error) {
        this.$message.error("Error al actualizar el estado de la empresa");
      }
      this.$emit("onCloseModal");
    },
    async handleDisabledEntitie() {
      try {
        const body = {
          status: UserStatus.inactive,
        };
        await this.$store.dispatch("updateStatusEntitie", {
          id: this.record.id,
          params: body,
        });
      } catch (error) {
        this.$message.error("Error al actualizar el estado de la empresa");
      }
      this.$emit("onCloseModal");
    },
    handleConfirmationEntitieInactive() {
      Modal.confirm({
        title: `¿Estás seguro de inactivar la empresa ”${this.record.name}”?`,
        content: "Recuerda que también se inactivará toda actividad asociada a esta empresa.",
        icon: createVNode(QuestionCircleOutlined),
        onOk: () => this.handleDisabledEntitie(),
        centered: true,
        okText: "Inactivar",
        cancelText: "Cancelar",
      });
    },

    setFieldsValue() {
      this.codePhone = this.record.code_phone?.id || 1;
      this.imageSrc = this.record.logo;
    },
    handleOk() {
      this.formRef
        .validateFields()
        .then(async () => {
          this.loading = true;
          const values = toRaw(this.formState);
          const formData = new FormData();
          if (this.imageFile) formData.append("logo", this.imageFile);
          formData.append("name", values.name);
          formData.append("phone", values.phone || "");
          if (values.phone) formData.append("code_phone", this.codePhone || "");
          formData.append("email", values.email || "");
          formData.append("country", this.country || "");
          formData.append("type_industry", values.type_industry);
          formData.append("imageSrc", values.imageSrc || "");
          formData.append("description", this.formState.description);
          formData.append("keywords", JSON.stringify(this.formState.keywords));
          await this.$store.dispatch("updateEntity", {
            entityId: this.record.id,
            entity: formData,
          });
          this.selectedImage = false;
          this.$message.success(`Se han guardado los cambios correctamente`);
          this.resetFields();
          this.$emit("update");
          this.$emit("onCloseModal");
        })
        .catch((error) => {
          this.$message.error("No se pudo actualizar");
          console.error(error);
        });
    },
    resetFields() {
      this.formRef.resetFields();
      this.loading = false;
      this.codePhone = 1;
      this.imageFile = undefined;
    },
    handleSelectedImagen(e) {
      this.selectedImage = e;
    },
    handleDropdownVisibleChange(open) {
      clearTimeout(this.dropdownCloseTimer);
      if (open) {
        this.isAnyDropdownOpen = true;
      } else {
        this.dropdownCloseTimer = setTimeout(() => {
          this.isAnyDropdownOpen = false;
        }, 100);
      }
    },
    preventScroll(e) {
      if (this.isAnyDropdownOpen) {
        if (e.target.closest('.ant-select-dropdown')) return;
        e.preventDefault();
        e.stopImmediatePropagation();
      }
    },
  },
});
</script>

<style lang="sass" scoped>
.form
  display: block
  max-width: 450px
  margin: 0 auto

.label
  display: block
  margin-bottom: 8px

.ant-form-item
  margin-bottom: 12px

  .textarea
    position: relative
    border: 1px solid $gray-5
    width: 276px
    border-radius: 4px
    width: 100%

    &::placeholder
      color: $gray-6
    &:hover
      border: 1px solid $blue-5
    &:focus
      border: 1px solid $blue-5

  .counter
    position: absolute !important
    right: 12px
    bottom: 12px
    font-size: 12px
    color: $gray-6

.flag
  width: 20px
  margin-bottom: 2px
  margin-right: 4px

.info-circle
  width: 120px

:deep(.ant-modal-body)
  padding: 24px 0

.control__user
  display: flex
  justify-content: flex-end

.user
  &--inactive
    color: #8c8c8c
    margin-right: 10px

  &--reactive
    color: #8c8c8c
    margin-right: 10px

  &--delete
    color: #F5222D

// .colorStatus
//   margin-top: -15px

.required
  color: $red-4
  margin-right: 3px
  font-size: 12px
</style>
